import type { AxiosError } from 'axios';

import { parseApiError } from '../../api/request';
import { captureException as captureLogRocketException, initializeLogRocket } from '../logrocket';
import {
    captureException as captureSentryException,
    captureMessage as captureSentryMessage,
    initializeSentry,
    withScope as withSentryScope,
} from '../sentry';

export const initializeErrorTracking = (sentryDsn: string, logRocketDsn: string) => {
    const env = config.env;

    initializeSentry(env, sentryDsn);
    initializeLogRocket(logRocketDsn);
};

export const isRequestCancelled = (error: Error): boolean =>
    error != null &&
    typeof error === 'object' &&
    ((error as { name?: string }).name === 'AbortError' ||
        (error as { name?: string }).name === 'CanceledError' ||
        (error as { code?: string }).code === 'ERR_CANCELED');

const isLocalEnv = (): boolean => config.env === 'local';

const shouldSkipCapture = (error: Error): boolean =>
    // Do not capture request cancellation - it is intentional, not a crash
    isRequestCancelled(error) ||
    // Do not capture errors in local environment
    isLocalEnv();

const isHttp4xx = (status?: number): boolean => status != null && status >= 400 && status < 500;

// Ids, hashes and emails in the path would split issues per record and leak PII into tags
const ID_PATH_SEGMENT = /^\d+$|^[0-9a-f-]{16,}$|@|%40/i;

const getEndpoint = ({ config }: AxiosError): string | undefined => {
    if (!config?.url) {
        return undefined;
    }

    const path = new URL(config.url, window.location.origin).pathname
        .split('/')
        .map((segment) => (ID_PATH_SEGMENT.test(segment) ? ':id' : segment))
        .join('/');

    return `${config.method?.toUpperCase()} ${path}`;
};

export const captureException = (error: Error) => {
    if (shouldSkipCapture(error)) {
        return null;
    }

    const status = (error as AxiosError).response?.status;

    // Do not capture 401 errors - these are expected auth/session-expired flows
    if (status === 401) {
        return null;
    }

    const apiError = parseApiError(error);
    const endpoint = getEndpoint(error as AxiosError);
    const context = { tags: { api: true, code: apiError?.code ?? status, endpoint } };

    // Expected 4xx go as messages: without an exception Sentry does not record an error replay
    if (isHttp4xx(status)) {
        captureSentryMessage(error.message, {
            ...context,
            level: 'error',
            fingerprint: ['api-4xx', String(status), endpoint ?? 'unknown'],
        });
    } else {
        captureSentryException(error, context);
    }
    captureLogRocketException(error, context);
};

export const captureUnhandledError = (error: Error, info, callback: (id: string) => void) => {
    if (shouldSkipCapture(error)) {
        return null;
    }

    withSentryScope((scope) => {
        scope.setExtras(info);
        const eventId = captureSentryException(error);
        callback(eventId);
    });
    captureLogRocketException(error, { extra: info });
};
